import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { withRights } from 'providers/RightsConsumer';
import { SELECT, TP_NUM } from 'constants/inputTypes';

const OfferAssetFinancialComponent = React.memo(
  ({
    t,
    currencies,
    rights,
    onPropertyChange,
    acquisitionPriceWithoutVat,
    reevaluationPriceWithoutVat,
    currencyId,
    acquisitionPriceWithVat,
    acquisitionPriceWithoutVatError,
    acquisitionPriceWithVatError,
    processing,
    addToRefs
  }) => {
    const handleAcquisitionPriceWithoutVatChange = useCallback(
      value => onPropertyChange('acquisitionPriceWithoutVat')(value),
      [onPropertyChange]
    );

    const handleAcquisitionPriceWithVatChange = useCallback(
      value => onPropertyChange('acquisitionPriceWithVat')(value),
      [onPropertyChange]
    );

    const handleReevaluationPriceWithoutVatChange = useCallback(
      value => onPropertyChange('reevaluationPriceWithoutVat')(value),
      [onPropertyChange]
    );

    return (
      <>
        <Grid
          item
          xs={9}
          md={10}
          id={t('OfferEdit.Asset.Financial.AcquisitionPriceWithoutVat')}
          ref={ref => addToRefs(ref, t('OfferEdit.Asset.Financial.AcquisitionPriceWithoutVat'), TP_NUM)}
        >
          <TextField
            id={'offerAssetCard_financial_acquisitionPriceWithoutVat'}
            required
            fullWidth
            isNumeric
            error={Boolean(acquisitionPriceWithoutVatError)}
            helperText={acquisitionPriceWithoutVatError}
            value={acquisitionPriceWithoutVat}
            debounceBy={400}
            onChange={handleAcquisitionPriceWithoutVatChange}
            label={t('OfferEdit.Asset.Financial.AcquisitionPriceWithoutVat')}
            disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
          />
        </Grid>
        <Grid
          item
          xs={3}
          md={2}
          id={t('OfferEdit.Asset.Financial.Currency')}
          ref={ref => addToRefs(ref, t('OfferEdit.Asset.Financial.Currency'), SELECT)}
        >
          <Autocomplete
            id={'offerAssetCard_financial_currency'}
            fullWidth
            required
            labelKey='code'
            valueKey='currencyId'
            value={currencyId}
            isSearchable={false}
            simpleValue={true}
            isClearable={false}
            onChange={onPropertyChange('currencyId')}
            options={currencies}
            disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
            label={t('OfferEdit.Asset.Financial.Currency')}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={10}
          id={t('OfferEdit.Asset.Financial.AcquisitionPriceWithVat')}
          ref={ref => addToRefs(ref, t('OfferEdit.Asset.Financial.AcquisitionPriceWithVat'), TP_NUM)}
        >
          <TextField
            id={'offerAssetCard_financial_acquisitionPriceWithVat'}
            required
            fullWidth
            isNumeric
            error={Boolean(acquisitionPriceWithVatError)}
            helperText={acquisitionPriceWithVatError}
            value={acquisitionPriceWithVat}
            debounceBy={400}
            onChange={handleAcquisitionPriceWithVatChange}
            label={t('OfferEdit.Asset.Financial.AcquisitionPriceWithVat')}
            disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
          />
        </Grid>
        <Grid
          hidden={true}
          item
          xs={9}
          md={10}
          id={t('OfferEdit.Asset.Financial.ReevaluationPriceWithoutVat')}
          ref={ref => addToRefs(ref, t('OfferEdit.Asset.Financial.ReevaluationPriceWithoutVat'), TP_NUM)}
        >
          <TextField
            id={'offerAssetCard_financial_reevaluationPriceWithoutVat'}
            fullWidth
            isNumeric
            value={reevaluationPriceWithoutVat}
            debounceBy={400}
            onChange={handleReevaluationPriceWithoutVatChange}
            label={t('OfferEdit.Asset.Financial.ReevaluationPriceWithoutVat')}
            disabled={rights.readOnlyFull || !rights.financedAssetWrite || processing}
          />
        </Grid>
      </>
    );
  }
);

OfferAssetFinancialComponent.displayName = 'OfferAssetFinancialComponent';

OfferAssetFinancialComponent.propTypes = {
  t: PropTypes.func.isRequired,
  currencies: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  acquisitionPriceWithoutVat: PropTypes.number,
  reevaluationPriceWithoutVat: PropTypes.number,
  currencyId: PropTypes.number,
  acquisitionPriceWithVat: PropTypes.number,
  acquisitionPriceWithoutVatError: PropTypes.string,
  acquisitionPriceWithVatError: PropTypes.string,
  processing: PropTypes.bool,
  addToRefs: PropTypes.func.isRequired
};

const MemoizedOfferAssetFinancialComponent = withTranslation()(withRights(OfferAssetFinancialComponent));

const ForwardedOfferAssetFinancialComponent = forwardRef((props, ref) => (
  <MemoizedOfferAssetFinancialComponent {...props} forwardedRef={ref} />
));

ForwardedOfferAssetFinancialComponent.displayName = 'ForwardedOfferAssetFinancialComponent';

export default ForwardedOfferAssetFinancialComponent;
